import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    ChevronLeft, User, LogOut, LayoutDashboard, UserCircle, Globe, Mail, ShieldCheck,
    Zap, History, CreditCard, Monitor, Wrench, Users, Settings2, ArrowUpRight
} from 'lucide-react';
import { getAuth } from '../helper/helperFirebaseAuth';
import MainLogo from './../components/mainLogo';
import { useAuth } from './../components/authProvider';
import SupportToast from './../components/supportToast';
import SEModal from './../components/seModal';


const _NAVIGATION = [
    { title: "Dashboard", url: "/", icon: LayoutDashboard },
    { title: "Your Profile", url: "/profile", icon: UserCircle },
    { title: "Members", url: "/members", icon: Users },
    { isBreaker: true },
    { title: "Domains", url: "/domains", icon: Globe },
    { title: "Email Rules", url: "/rules", icon: ShieldCheck },
    { title: "Email Delivery", url: "/deliveries", icon: Mail },
    { isBreaker: true },
    { title: "History", url: "/history", icon: History },
    { title: "Billing", url: "/billing", icon: CreditCard },
    { isBreaker: true },
    { title: "Microsoft Exchange", url: "/exchange", icon: Settings2 },
    { title: "Monitoring", url: "/monitoring", icon: Monitor },
    { forAdmin: true, isBreaker: true },
    { forAdmin: true, title: "Admin Dashboard", url: "/admin/dashboard", icon: LayoutDashboard },
    { forAdmin: true, title: "Tools", url: "/admin/tools", icon: Wrench },
];

const MainLayout = ({ children }) => {
    const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
    const urlWebsite = import.meta.env.VITE_URL_WEB || "https://sparklingemail.com";
    const currentLocation = useLocation();
    const navigate = useNavigate();
    const { authUser, authUserEx } = useAuth();

    const handleSignOut = async () => {
        SEModal.fire({
            type: "warning",
            title: "Sign Out",
            description: "Are you sure you want to sign out?",
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: "Yes, Sign Out",
            cancelButtonText: "Cancel"
        }).then(async (result) => {
            if (result.isConfirmed) {
                await getAuth().signOut();
                navigate('/login', { replace: true });
            }
        });
    };

    return <>
        <div className="flex h-screen bg-slate-50">
            {/* --- Sidebar --- */}
            <aside className={`bg-white transition-all duration-300 ease-in-out flex flex-col overflow-y-auto flex-shrink-0 ${sidebarCollapsed ? 'w-15' : 'w-72'}`}>
                <div className="flex items-center justify-between h-16 p-3 border-b">
                    {!sidebarCollapsed && (
                        <Link to={urlWebsite} className="inline-flex items-center space-x-2">
                            <MainLogo />
                            <span className="text-xl font-bold bg-gradient-to-r bg-clip-text text-transparent animate-gradient from-blue-600 via-teal-600 to-yellow-400">
                                Sparkling Email
                            </span>
                        </Link>
                    )}
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className="p-2 rounded-lg hover:bg-slate-100 focus:outline-none select-none cursor-pointer"
                        title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                        aria-label={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                        tabIndex={0}
                        role="button"
                    >
                        <ChevronLeft className={`w-6 h-6 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`} />
                    </button>
                </div>

                <nav className="flex-1 px-3 py-6 space-y-2">
                    {_NAVIGATION.map((navItem, index) => {
                        const isActive = (navItem.url === currentLocation.pathname);
                        if (navItem?.forAdmin !== true || authUserEx?.isSuperAdmin === true) {
                            return (navItem?.isBreaker)
                                ? <hr key={index} className="my-2 border-t border-slate-200" />
                                : <Link
                                    key={index}
                                    to={navItem.url}
                                    className={`flex items-center text-sm p-2 rounded-lg transition-colors ${isActive ? 'bg-slate-400 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                                    title={navItem.title}
                                    aria-label={navItem.title}
                                >
                                    <navItem.icon
                                        className="w-5 h-5 focus:outline-none select-none"
                                        aria-hidden="true"
                                        title={navItem.title}
                                        tabIndex={-1}
                                    />
                                    {!sidebarCollapsed && (
                                        <span className={`ml-4 ${isActive ? 'font-semibold' : 'font-normal'}`}>
                                            {navItem.title}
                                        </span>
                                    )}
                                </Link>
                        }
                    })}
                </nav>

                {!sidebarCollapsed && (
                    <div className={"border border-slate-200 rounded-lg p-3 m-3 text-sm " + authUserEx?.currentPlan?.style}>
                        <div className="flex justify-between items-center">
                            <span className="font-semibold">{authUserEx?.currentPlan?.label}</span>
                            {authUserEx?.currentPlan?.shouldSuggestUpgrade &&
                                <Link
                                    to="/billing#subscription"
                                    title="Upgrade Plan"
                                    aria-label="Upgrade Plan"
                                    role="button"
                                    className="hover:underline flex items-center text-slate-600 font-medium"
                                >
                                    Upgrade <ArrowUpRight className="w-4 h-4 ml-1" />
                                </Link>}
                        </div>
                    </div>
                )}
                <div className="mt-auto p-3 border-t border-slate-200">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            {authUser?.photoURL
                                ? <img src={authUser.photoURL} alt="User Profile" className="w-10 h-10 rounded-full" />
                                : <UserCircle className="w-10 h-10 text-slate-500" />}
                        </div>
                        {!sidebarCollapsed && (
                            <div className="ml-3 text-sm min-w-0">
                                <div className="font-semibold text-slate-800 truncate">{authUser?.displayName}</div>
                                <div className="text-slate-500 truncate">{authUser?.email}</div>
                            </div>
                        )}
                        <button
                            className="ml-auto p-2 rounded-lg hover:bg-slate-100 focus:outline-none select-none cursor-pointer"
                            onClick={handleSignOut}
                            title="Sign Out"
                            aria-label="Sign Out"
                            tabIndex={0}
                            role="button"
                        >
                            <LogOut className="w-5 h-5 text-slate-500" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* --- Main Content & Header --- */}
            <div className="flex-1 flex flex-col">
                {/* --- Page Content --- */}
                <main className="flex-1 overflow-y-auto">
                    <div className="p-4 sm:p-6 lg:px-12 lg:py-6 space-y-4">
                        {children}
                    </div>

                    {/* --- Need Help Toast --- */}
                    <SupportToast />
                </main>
            </div>
        </div>
    </>
};

export default MainLayout;